import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
} from '@/components/ui/form'
import {
  SearchDropdown,
  type SearchDropdownItem,
} from '@/components/ui/search-dropdown'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { integrationsClient } from '@/lib/api/integrations/integrations.client'
import { mapStatusToItem } from '@/lib/api/integrations/rent-manager.utils'
import { toast } from 'sonner'
import { FloatingActionIslandContent } from '@/components/ui/FloatingActionIslandContent'
import { Button } from '@/components/ui/button'
import {
  INTEGRATIONS,
  type RentManagerLocationOverview,
} from '@super/shared-types'
import { useAppAuth } from '@/contexts/app-auth.context'
import { useAuth } from '@/hooks/use-auth'

const adminFormSchema = z.object({
  configId: z.string().min(1, 'Config ID is required'),
  defaultStatusId: z.string().optional(),
})

const userFormSchema = z.object({
  defaultStatusId: z.string().optional(),
})

type AdminFormValues = z.infer<typeof adminFormSchema>
type UserFormValues = z.infer<typeof userFormSchema>
type FormValues = AdminFormValues | UserFormValues

export interface RentManagerConnectFormProps {
  isEditMode?: boolean
  onConnected: () => void
  onCancel?: () => void
}

export function RentManagerConnectForm({
  isEditMode = false,
  onConnected,
  onCancel,
}: RentManagerConnectFormProps) {
  const { currentCompany } = useAppAuth()
  const { isSuperAdmin, isLoading: isAuthLoading } = useAuth()
  const [existingConfigId, setExistingConfigId] = useState<string>('')
  const [existingDefaultStatusId, setExistingDefaultStatusId] =
    useState<string>('')
  const [isLoading, setIsLoading] = useState(isEditMode)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isConnected, setIsConnected] = useState(false)

  const [statusItems, setStatusItems] = useState<SearchDropdownItem<number>[]>(
    [],
  )
  const [isLoadingDropdowns, setIsLoadingDropdowns] = useState(false)
  const [dropdownsFailed, setDropdownsFailed] = useState(false)

  const [locations, setLocations] = useState<RentManagerLocationOverview[]>([])
  const [selectedLocationIds, setSelectedLocationIds] = useState<number[]>([1])
  const [existingLocationIds, setExistingLocationIds] = useState<number[]>([1])
  const [locationsFailed, setLocationsFailed] = useState(false)

  const form = useForm<FormValues>({
    resolver: zodResolver(isSuperAdmin ? adminFormSchema : userFormSchema),
    mode: 'onChange',
    defaultValues: isSuperAdmin
      ? { configId: '', defaultStatusId: '' }
      : { defaultStatusId: '' },
  })

  useEffect(() => {
    if (!currentCompany?.id || isAuthLoading) return
    if (!isEditMode) {
      setIsLoading(false)
      return
    }

    const fetchStatus = async () => {
      try {
        setIsLoading(true)
        const integrations = isSuperAdmin
          ? await integrationsClient.getAllAdminIntegrationsStatus(
              currentCompany.id,
            )
          : await integrationsClient.getAllIntegrationsStatus()
        const rentManager = integrations.find(
          i =>
            i.type === INTEGRATIONS.RENT_MANAGER.TYPE ||
            i.name === INTEGRATIONS.RENT_MANAGER.NAME,
        )
        if (rentManager?.connected) {
          setIsConnected(true)
          const configId = (rentManager.metadata?.configId as string) || ''
          const defaultStatusId =
            (rentManager.metadata?.defaultStatusId as string) || ''
          setExistingConfigId(configId)
          setExistingDefaultStatusId(defaultStatusId)
          const rawLocationIds = rentManager.metadata?.locationIds
          const locationIds =
            Array.isArray(rawLocationIds) && rawLocationIds.length > 0
              ? (rawLocationIds.filter(v => typeof v === 'number') as number[])
              : [1]
          setExistingLocationIds(locationIds)
          setSelectedLocationIds(locationIds)
          if (isSuperAdmin) {
            form.reset({ configId, defaultStatusId })
          } else {
            form.reset({ defaultStatusId })
          }
        }
      } catch (error) {
        console.error('Error fetching integration status:', error)
        toast.error('Failed to load existing configuration')
      } finally {
        setIsLoading(false)
      }
    }
    fetchStatus()
  }, [isEditMode, currentCompany?.id, isSuperAdmin, isAuthLoading, form])

  useEffect(() => {
    if (!currentCompany?.id || isAuthLoading || !isConnected) return

    let cancelled = false
    setIsLoadingDropdowns(true)
    setDropdownsFailed(false)

    const request = isSuperAdmin
      ? integrationsClient.rentManager.getAdminStatuses(currentCompany.id)
      : integrationsClient.rentManager.getStatuses()

    request
      .then(statuses => {
        if (cancelled) return
        setStatusItems(
          statuses.map(mapStatusToItem).filter(item => item !== null),
        )
      })
      .catch(error => {
        if (cancelled) return
        console.error('Error fetching statuses:', error)
        setDropdownsFailed(true)
        toast.error(
          'Unable to fetch statuses from Rent Manager. Please try again later.',
        )
      })
      .finally(() => !cancelled && setIsLoadingDropdowns(false))

    return () => {
      cancelled = true
    }
  }, [currentCompany?.id, isSuperAdmin, isAuthLoading, isConnected])

  useEffect(() => {
    if (!currentCompany?.id || isAuthLoading || !isConnected || !isSuperAdmin)
      return

    let cancelled = false
    setLocationsFailed(false)

    integrationsClient.rentManager
      .getAdminLocations(currentCompany.id)
      .then(result => {
        if (cancelled) return
        setLocations(result)
      })
      .catch(error => {
        if (cancelled) return
        console.error('Error fetching locations:', error)
        setLocationsFailed(true)
      })

    return () => {
      cancelled = true
    }
  }, [currentCompany?.id, isSuperAdmin, isAuthLoading, isConnected])

  const toggleLocation = (locationId: number, checked: boolean) => {
    setSelectedLocationIds(prev => {
      if (checked) {
        return prev.includes(locationId)
          ? prev
          : [...prev, locationId].sort((a, b) => a - b)
      }
      const next = prev.filter(id => id !== locationId)
      // Never allow an empty selection — the sync needs at least one database
      return next.length > 0 ? next : prev
    })
  }

  const handleSubmit = async (data: FormValues) => {
    if (!currentCompany?.id) {
      toast.error('No company selected')
      return
    }

    try {
      setIsSubmitting(true)

      if (isSuperAdmin) {
        const adminData = data as AdminFormValues
        const metadata = {
          configId: adminData.configId,
          defaultStatusId: adminData.defaultStatusId || undefined,
          locationIds: [...selectedLocationIds].sort((a, b) => a - b),
        }

        if (isEditMode) {
          await integrationsClient.rentManager.updateAdminMetadata(
            currentCompany.id,
            metadata,
          )
          toast.success('Rent Manager configuration updated successfully!')
        } else {
          await integrationsClient.rentManager.createAdminMetadata(
            currentCompany.id,
            metadata,
          )
          toast.success('Rent Manager integration connected successfully!')
        }
      } else {
        const userData = data as UserFormValues
        await integrationsClient.rentManager.updateMetadata({
          defaultStatusId: userData.defaultStatusId || undefined,
        })
        toast.success('Rent Manager settings updated successfully!')
      }

      onConnected()
    } catch (error) {
      console.error('Error saving Rent Manager settings:', error)
      toast.error('Failed to save Rent Manager settings')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isAuthLoading || isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-20 bg-secondary/50 animate-pulse rounded" />
      </div>
    )
  }

  if (!isSuperAdmin && !isEditMode) {
    return (
      <div className="space-y-6">
        <div className="max-w-md rounded-lg border border-amber-200 bg-amber-50 p-4">
          <p className="text-sm text-amber-800">
            An admin from Super needs to enable this securely. Contact
            help@hiresuper.com to confirm next steps.
          </p>
        </div>
        {onCancel && (
          <Button variant="outline" onClick={onCancel}>
            Back to Integrations
          </Button>
        )}
      </div>
    )
  }

  const watchedValues = form.watch()
  const currentConfigId = isSuperAdmin
    ? (watchedValues as AdminFormValues).configId
    : existingConfigId
  const currentDefaultStatusId =
    (watchedValues as UserFormValues).defaultStatusId || ''

  const locationIdsChanged =
    [...selectedLocationIds].sort((a, b) => a - b).join(',') !==
    [...existingLocationIds].sort((a, b) => a - b).join(',')

  const hasChanges = isSuperAdmin
    ? currentConfigId !== existingConfigId ||
      currentDefaultStatusId !== existingDefaultStatusId ||
      locationIdsChanged
    : currentDefaultStatusId !== existingDefaultStatusId

  return (
    <>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(handleSubmit)}
          noValidate
          className="space-y-6 max-w-2xl"
        >
          {isSuperAdmin && (
            <FormField
              control={form.control}
              name={'configId' as never}
              render={({ field }) => (
                <FormItem className="max-w-md">
                  <FormLabel>Rent Manager Config ID</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="abc123"
                      disabled={isSubmitting}
                      autoFocus
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    The configuration ID for your Rent Manager instance
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {isConnected && (
            <FormField
              control={form.control}
              name={'defaultStatusId' as never}
              render={({ field }) => {
                const value = (field.value as string) ?? ''
                const selected =
                  statusItems.find(item => item.id === value) ?? null
                return (
                  <FormItem className="max-w-md">
                    <FormLabel>Default Work Order Status</FormLabel>
                    <FormControl>
                      {dropdownsFailed ? (
                        <Input
                          placeholder="e.g. 12345"
                          value={value}
                          onChange={e => field.onChange(e.target.value)}
                          disabled={isSubmitting}
                        />
                      ) : (
                        <SearchDropdown<number>
                          items={statusItems}
                          selectedItems={selected}
                          onSelect={item => {
                            const sel = Array.isArray(item) ? item[0] : item
                            field.onChange(
                              sel?.data != null ? String(sel.data) : '',
                            )
                          }}
                          buttonLabel={
                            isLoadingDropdowns
                              ? 'Loading statuses...'
                              : (selected?.label ?? 'Select a status...')
                          }
                          placeholder="Select a status..."
                          searchPlaceholder="Search statuses..."
                          emptyMessage="No statuses found"
                          buttonClassName="w-full [&_.truncate]:ml-0"
                          disabled={isSubmitting || isLoadingDropdowns}
                        />
                      )}
                    </FormControl>
                    <FormDescription>
                      The initial status assigned to new work orders
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )
              }}
            />
          )}

          {isSuperAdmin &&
            isConnected &&
            (locations.length > 1 ||
              (locationsFailed && existingLocationIds.length > 1)) && (
              <div className="max-w-md space-y-2">
                <FormLabel>Locations to sync</FormLabel>
                {locationsFailed ? (
                  <p className="text-sm text-muted-foreground">
                    Unable to load locations from Rent Manager. Currently
                    syncing: {existingLocationIds.join(', ')}
                  </p>
                ) : (
                  <div className="rounded-md border divide-y">
                    {locations.map(location => (
                      <label
                        key={location.locationId}
                        className={`flex items-center gap-2.5 px-3 py-1.5 text-sm ${
                          location.authorized
                            ? 'cursor-pointer'
                            : 'opacity-60 cursor-not-allowed'
                        }`}
                      >
                        <Checkbox
                          checked={selectedLocationIds.includes(
                            location.locationId,
                          )}
                          disabled={!location.authorized || isSubmitting}
                          onCheckedChange={checked =>
                            toggleLocation(
                              location.locationId,
                              checked === true,
                            )
                          }
                        />
                        <span className="flex-1 truncate">
                          {location.name}
                          {location.isMainLocation && (
                            <span className="text-muted-foreground">
                              {' '}
                              (default)
                            </span>
                          )}
                        </span>
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          {location.authorized
                            ? `${location.propertyCount ?? '?'} properties`
                            : location.probeFailed
                              ? 'Couldn’t check'
                              : 'Not authorized'}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
                <FormDescription>
                  Checked locations sync into the CRM.
                  {locations.some(location => !location.authorized) &&
                    ' Grayed-out locations haven’t been authorized for Super in Rent Manager yet. Enable them under the integration’s settings in Rent Manager to make them selectable here.'}
                </FormDescription>
              </div>
            )}
        </form>
      </Form>

      <FloatingActionIslandContent
        show={hasChanges}
        componentId="rent-manager-form"
        priority={20}
      >
        <div className="inline-flex w-full max-w-2xl">
          <div className="flex flex-grow items-center space-x-2 text-sm text-gray-600 whitespace-nowrap pointer-events-none mr-4">
            <span>
              {isEditMode
                ? 'Update your Rent Manager configuration'
                : 'Connect your Rent Manager integration'}
            </span>
          </div>

          <div className="flex items-center space-x-2 justify-end pointer-events-auto ml-auto">
            <Button
              onClick={form.handleSubmit(handleSubmit)}
              disabled={isSubmitting}
              size="sm"
              variant="form"
            >
              <span>
                {isSubmitting ? 'Saving...' : isEditMode ? 'Update' : 'Connect'}
              </span>
            </Button>
          </div>
        </div>
      </FloatingActionIslandContent>
    </>
  )
}
